// src/pages/PriceList.js
import React, { useState, useEffect } from 'react';
import { T, Card, Btn, SectionTitle, Modal, Spinner, Empty } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import { listProducts, createProduct, updateProduct, deleteProduct } from '../services/api';

const INP = { background:T.surface, border:`1px solid ${T.border}`, borderRadius:'8px', padding:'10px 14px', color:T.text, fontSize:'14px', width:'100%', outline:'none', boxSizing:'border-box', marginBottom:'12px' };
const LBL = { fontSize:'11px', color:T.muted, marginBottom:'5px', display:'block', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px' };

export default function PriceList() {
  const { can } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=closed, {}=new, {...product}=edit
  const [msg, setMsg] = useState(null);

  const load = () => {
    setLoading(true);
    listProducts()
      .then(r => setProducts(r.data.data))
      .catch(() => setMsg({ type:'error', text:'Failed to load price list.' }))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the price list?`)) return;
    try {
      await deleteProduct(p.id);
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.response?.data?.message || 'Failed to remove product.' });
    }
  };

  return (
    <div>
      <SectionTitle sub="Products and services staff can add to orders. Prices are fixed — staff cannot change them.">Price List</SectionTitle>

      {msg && <div style={{ color:T.danger, fontSize:'13px', marginBottom:'14px' }}>{msg.text}</div>}

      {can('manage_price_list') && (
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'14px' }}>
          <Btn onClick={() => setEditing({})}>+ Add product / service</Btn>
        </div>
      )}

      {loading ? <Spinner /> : products.length === 0 ? (
        <Empty msg="No products yet. Add your first product to the price list." />
      ) : (
        <Card style={{ padding:0, overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Name</th>
                <th style={TH}>Description</th>
                <th style={TH}>Unit</th>
                <th style={TH}>Price (KES)</th>
                {can('manage_price_list') && <th style={TH}></th>}
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} style={{ opacity: p.is_active ? 1 : 0.4 }}>
                  <td style={TD}><b>{p.name}</b></td>
                  <td style={TD}>{p.description || '—'}</td>
                  <td style={TD}>{p.unit}</td>
                  <td style={{ ...TD, fontWeight:700, color:T.gold }}>
                    {Number(p.price).toLocaleString()}
                  </td>
                  {can('manage_price_list') && (
                    <td style={TD}>
                      <div style={{ display:'flex', gap:'8px' }}>
                        <Btn size="sm" variant="outline" onClick={() => setEditing(p)}>Edit</Btn>
                        <Btn size="sm" variant="danger" onClick={() => handleDelete(p)}>Remove</Btn>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {editing !== null && (
        <ProductModal
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ProductModal({ product, onClose, onSaved }) {
  const isNew = !product.id;
  const [form, setForm] = useState({
    name: product.name || '',
    description: product.description || '',
    price: product.price !== undefined ? String(product.price) : '',
    unit: product.unit || 'piece',
  });
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setMsg(null);
    if (!form.name.trim()) return setMsg('Product name is required.');
    const price = Number(form.price);
    if (!Number.isFinite(price) || price < 0) return setMsg('Enter a valid price (0 or more).');

    setLoading(true);
    try {
      if (isNew) {
        await createProduct({ name: form.name.trim(), description: form.description, price, unit: form.unit });
      } else {
        await updateProduct(product.id, { name: form.name.trim(), description: form.description, price, unit: form.unit });
      }
      onSaved();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to save.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={isNew ? 'Add product / service' : `Edit "${product.name}"`} onClose={onClose}>
      <label style={LBL}>Name</label>
      <input style={INP} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. A4 Flyers (full colour, glossy)" />

      <label style={LBL}>Description (optional)</label>
      <input style={INP} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Extra details for the invoice" />

      <label style={LBL}>Unit</label>
      <select style={INP} value={form.unit} onChange={e => set('unit', e.target.value)}>
        {['piece','page','set','pack','ream','roll','kg','hour'].map(u => (
          <option key={u} value={u}>{u}</option>
        ))}
      </select>

      <label style={LBL}>Price per unit (KES)</label>
      <input style={INP} type="number" min="0" step="0.01" value={form.price} onChange={e => set('price', e.target.value)} placeholder="e.g. 5000" />

      {msg && <div style={{ color:T.danger, fontSize:'12px', marginBottom:'12px' }}>{msg}</div>}

      <Btn onClick={submit} disabled={loading}>{loading ? 'Saving...' : (isNew ? 'Add to price list' : 'Save changes')}</Btn>
    </Modal>
  );
}

const TH = { textAlign:'left', padding:'10px 14px', fontSize:'11px', color:T.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', borderBottom:`1px solid ${T.border}` };
const TD = { padding:'11px 14px', fontSize:'13px', borderBottom:`1px solid ${T.surface}` };
